import type { GoodsProperty } from '@/types/buy';

/** Неразрывный пробел — разделитель тысяч (цена не переносится). */
const NBSP = ' ';

// Денежный формат: целые ₽/т с разделением тысяч неразрывным пробелом. 14000 → «14 000».
// Делаем вручную (не toLocaleString), чтобы результат не зависел от ICU-локали среды.
export function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.round(value)).toString();
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * Денежная величина с копейками, когда они есть: 7.24 → «7,24», 8 → «8», 1234.5 → «1 234,50».
 * Нужна там, где округление до рубля меняет смысл: ставка ₽/т·км измеряется единицами рублей, и
 * «7 ₽/т·км» вместо «7,24» — уже другая цена, а ставка ниже полтинника округлилась бы в ноль,
 * то есть в «везти даром».
 */
export function formatMoneyPrecise(value: number | null | undefined): string {
  // Number.isNaN пропускает бесконечность, а она дала бы «Infinity,00» на экране.
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const rounded = Math.round(Math.abs(value) * 100) / 100;
  if (Number.isInteger(rounded)) {
    // Значение меньше копейки округляется в ноль; знак при этом отбрасываем, иначе вышло бы «-0».
    return formatMoney(rounded === 0 ? 0 : value);
  }
  const [int = '0', frac = '00'] = rounded.toFixed(2).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${value < 0 ? '-' : ''}${grouped},${frac}`;
}

// Вес в тоннах: 2 знака после запятой, тысячи неразрывным пробелом, десятичная запятая. 4500 →
// «4 500,00», 899.95 → «899,95». Для таблиц «по факту» (дробные тонны).
export function formatTons(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const [int = '0', frac = '00'] = Math.abs(value).toFixed(2).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${value < 0 ? '-' : ''}${grouped},${frac}`;
}

// Тоннаж целыми тоннами с разделением тысяч и единицей: 6000 → «6 000 т». Для чипа объёма в виджетах.
export function formatWeightTon(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const grouped = Math.abs(Math.round(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${value < 0 ? '-' : ''}${grouped}${NBSP}т`;
}

/** Размер файла человекочитаемо: 12345 → «12 КБ», 0/undefined → «». */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {
    return '';
  }
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(i === 0 || value >= 10 ? 0 : 1)}${NBSP}${units[i]}`;
}

// Бэкенд присылает префиксы свойств как HTML-сущности («&ge;»/«&le;»). Декодируем базовый набор —
// безопасно (без innerHTML), только известные сущности.
const ENTITIES: Readonly<Record<string, string>> = {
  '&ge;': '≥',
  '&le;': '≤',
  '&gt;': '>',
  '&lt;': '<',
  '&amp;': '&',
  '&nbsp;': ' ',
};

export function decodeEntities(text: string | undefined | null): string {
  if (!text) {
    return '';
  }
  return text.replace(/&[a-z]+;/g, (match) => ENTITIES[match] ?? match);
}

/**
 * Значение показателя качества в русской записи: сервер присылает десятичную точку («12.5»), в тексте нужна
 * запятая («12,5»). Нечисловое значение возвращается как есть.
 */
export function formatQualityValue(value: string | number | undefined | null): string {
  if (value == null) {
    return '';
  }
  const text = String(value);
  return /^-?\d+\.\d+$/.test(text) ? text.replace('.', ',') : text;
}

/** Условие качества без названия: «≥ 12,5 %» — для подписей вариантов в выпадающих списках. */
export function formatPropertyCondition(property: Omit<GoodsProperty, 'name'>): string {
  return [decodeEntities(property.prefix), formatQualityValue(property.value), property.postfix]
    .filter((part) => part != null && part !== '')
    .join(' ');
}

/** Свойство качества в читаемый вид: «Протеин ≥ 15,3 %». */
export function formatProperty(property: Omit<GoodsProperty, 'name'> & { readonly name?: string }): string {
  return [property.name, formatPropertyCondition(property)].filter((part) => part != null && part !== '').join(' ');
}

/** Все свойства строки качества через запятую. */
export function formatQuality(properties: readonly GoodsProperty[]): string {
  return properties.map(formatProperty).join(', ');
}

// Приоритет «главного» (headline) параметра качества для шапки: протеин (пшеница) →
// клейковина → масличность; иначе первое свойство. Имя свойства приходит от бэка (RU).
const HEADLINE_QUALITY_PRIORITY: readonly RegExp[] = [/протеин/i, /клейковин/i, /масличн/i];

/** Главный параметр качества для шапки сделки (или первое свойство). undefined — свойств нет. */
export function headlineQuality(properties: readonly GoodsProperty[] | undefined): GoodsProperty | undefined {
  const list = properties ?? [];
  for (const re of HEADLINE_QUALITY_PRIORITY) {
    const match = list.find((p) => p.name != null && re.test(p.name));
    if (match) {
      return match;
    }
  }
  return list[0];
}

/**
 * Название организации с русскими кавычками: `ООО "БЮРО"` → «ООО «БЮРО»», `АО "Терминал "КСК""` → «АО «Терминал «КСК»»».
 * Только для показа: названия приходят с сервера с прямыми кавычками, а ключом запроса (например, `addressTitle`)
 * служит исходная строка. Кавычка открывающая, если стоит в начале или после пробела, скобки или другой открывающей;
 * иначе закрывающая. Если открывающих и закрывающих не поровну, строка возвращается без изменений — лучше прямые
 * кавычки, чем перекошенные.
 */
export function formatOrgName(name: string): string;
export function formatOrgName(name: string | undefined | null): string | undefined;
export function formatOrgName(name: string | undefined | null): string | undefined {
  if (name == null || !name.includes('"')) {
    return name ?? undefined;
  }
  let opened = 0;
  let result = '';
  for (const char of name) {
    if (char !== '"') {
      result += char;
      continue;
    }
    const previous = result.at(-1);
    if (previous === undefined || /[\s(«]/.test(previous)) {
      result += '«';
      opened += 1;
    } else {
      result += '»';
      opened -= 1;
      if (opened < 0) {
        return name;
      }
    }
  }
  return opened === 0 ? result : name;
}

/**
 * Подпись компании в выпадающем списке: «ООО «БЮРО» — ИНН 123». Одна функция и для вариантов поиска, и для
 * выбранного значения: после выбора Mantine подставляет подпись в строку поиска, и вызывающий код сравнивает её
 * с подписью выбранной компании, чтобы не отправлять её поисковым запросом. Разные способы сборки дали бы разные
 * строки, и подпись ушла бы на сервер.
 */
export function formatCompanyLabel(name: string | undefined | null, inn: string, innLabel: string): string {
  return `${formatOrgName(name) ?? '—'} — ${innLabel} ${inn}`;
}

const LEGAL_FORMS = new Set(['ООО', 'АО', 'ЗАО', 'ОАО', 'ПАО', 'НАО', 'ИП', 'КФХ', 'СПК']);

/** Инициалы организации из значимых слов: у «ООО "БЮРО"» — «БЮ», а не бессмысленные у всех «ОО». */
export function orgInitials(name: string): string {
  const words = name.replace(/["«»]/g, ' ').split(/\s+/).filter((word) => word && !LEGAL_FORMS.has(word.toUpperCase()));
  if (words.length === 0) {
    return name.slice(0, 2).toUpperCase();
  }
  return (words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : words[0]!.slice(0, 2)).toUpperCase();
}
